/* src/modules/it/components/MyAssets.js */
import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { CubeIcon } from '../../../components/icons/Icons';

export default function MyAssets({ userProfile, appId }) {
    const [assets, setAssets] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchAssets = async () => {
            if (!userProfile?.uid) return;

            try {
                // 1. Correct Collection Name: 'it_assets' instead of 'it_inventory'
                const ref = collection(db, `artifacts/${appId}/it_assets`);
                
                // 2. Correct Query Field: 'assignedToUserId' to match the database and security rules
                // We assume 'userProfile.uid' holds the auth ID.
                const q = query(
                    ref, 
                    where("assignedToUserId", "==", userProfile.uid)
                );
                
                const snap = await getDocs(q);
                setAssets(snap.docs.map(d => ({ id: d.id, ...d.data() })));
            } catch (e) { 
                console.error("Error fetching my assets:", e); 
            }
            setLoading(false);
        };
        fetchAssets();
    }, [appId, userProfile]);

    if (loading) return <div className="p-4 text-center text-gray-400">Loading your assets...</div>;

    return (
        <div className="animate-fadeIn">
            <h3 className="text-lg font-bold text-[#213368] mb-4">My Assigned Assets</h3>
            
            {assets.length === 0 ? (
                <div className="text-center py-10 border-2 border-dashed border-gray-200 rounded-xl bg-gray-50">
                    <CubeIcon className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="text-gray-500">No assets currently assigned to you.</p>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {assets.map((asset) => (
                        <div key={asset.id} className="bg-white border border-gray-200 p-5 rounded-lg shadow-sm hover:shadow-md transition-shadow">
                            {/* Header: Category & Asset Tag */}
                            <div className="flex justify-between items-start mb-3">
                                <span className={`px-2 py-1 rounded text-[10px] font-bold uppercase tracking-wider ${
                                    asset.category.includes('Laptop') 
                                        ? 'bg-blue-50 text-blue-700 border border-blue-100' 
                                        : 'bg-gray-100 text-gray-600 border border-gray-200'
                                }`}>
                                    {asset.category}
                                </span>
                                <span className="text-xs font-mono text-gray-400 bg-gray-50 px-2 py-1 rounded">
                                    {asset.assetTag}
                                </span>
                            </div>

                            {/* Main Info */}
                            <h4 className="font-bold text-[#213368] text-lg leading-tight">
                                {asset.vendor} {asset.model}
                            </h4>

                            {/* Specs (Only show if laptop) */}
                            {asset.category.includes('Laptop') && (asset.cpu || asset.ram) && (
                                <p className="text-xs text-gray-500 mt-1">
                                    {asset.cpu} • {asset.ram} • {asset.storage}
                                </p>
                            )}

                            {/* Assignment Details */}
                            <div className="mt-4 pt-3 border-t border-gray-100 flex flex-col space-y-1">
                                <p className="text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">Serial:</span> {asset.serialNumber}
                                </p>
                                <p className="text-xs text-gray-500">
                                    <span className="font-medium text-gray-700">Issued On:</span> {
                                        asset.assignmentDate 
                                        ? new Date(asset.assignmentDate).toLocaleDateString() 
                                        : 'N/A'
                                    }
                                </p>
                                {asset.description && (
                                    <p className="text-xs text-gray-400 italic mt-1">
                                        "{asset.description}"
                                    </p>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}